import { subMonths } from 'date-fns';
import { action } from 'typesafe-actions';
import { useParams } from 'react-router-dom';
import { Box } from '@dxb-web-ui-framework/design-system';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import React, { FC, useEffect, useCallback, useReducer, useState } from 'react';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { isEmpty, stringToDate } from '@dxb-web-ui-framework/common/utils';
import { cardService } from '../../../../../channels/secure-web/services';
import { ECOMMBXLoader } from '../../../../content-composer/src/components/Generic/ECOMMBXLoader/ECOMMBXLoader';
import { useSelectedCustomerId } from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { useCardHeaders } from '../../../../transaction/src/contexts/Parameters/hooks';
import { STATUS_DEFAULT_VALUE } from '../../components/CardTransactionsFiltersDrawer/useCardTransactionsFiltersConfig';
import { Card } from '../../domain/Card/entities/Card';
import { CardAction } from '../CardActions';
import { useCards } from '../Card';
import { CardDetailsReducer } from './CardDetailsReducer';
import { defaultCardDetailsProviderState } from './defaultState';
import { CardDetailsContext, CardDetailsDispatchContext } from './CardDetailsContext';
import { CardDetailsContextActionTypes, CardTransactionListFilters, CardDetailsProviderProps } from './types';

const CardDetailsProvider: FC<CardDetailsProviderProps> = ({
  children,
  cardListData,
  shouldNotFetchCardTransactions,
}) => {
  const { cardId, transactionNo } = useParams();
  const cardHeaders = useCardHeaders();
  const customerId = useSelectedCustomerId();
  // Get initial state values
  const [state, dispatch] = useReducer(CardDetailsReducer, defaultCardDetailsProviderState);
  const [timestamp, setTimestamp] = useState(new Date().toISOString());

  // Define api call conditions
  const shouldFetchCardDetails =
    (!isEmpty(state.selectedCard) || Boolean(state.selectedPaymentCardIdentifications)) && !!cardId;
  const shouldFetchCardTransactions =
    (!isEmpty(state.selectedCard) || Boolean(state.selectedPaymentCardIdentifications)) &&
    !!cardId &&
    !shouldNotFetchCardTransactions;
  // Define api calls
  const {
    data: cardDetailsData,
    error: cardDetailsDataError,
    isLoading: isLoadingCardDetailsData,
    mutate: refetchCardsDetailsDataMutation,
  } = useInvokeSWRService(
    cardService.GetCardDetails,
    {
      headers: cardHeaders,
      customerId,
      paymentCardIdentifications: state.selectedPaymentCardIdentifications,
    },
    shouldFetchCardDetails,
  );

  const {
    data: cardTransactionsData,
    error: cardTransactionsDataError,
    isLoading: isLoadingCardTransactionsData,
    mutate: refetchCardsTransactionsDataMutation,
  } = useInvokeSWRService(
    cardService.GetCardTransactions,
    {
      headers: cardHeaders,
      customerId,
      paymentCardIdentifications: state.selectedPaymentCardIdentifications,
      filters: {
        ...state.cardTransactionsFilters,
        status:
          state.cardTransactionsFilters?.status === STATUS_DEFAULT_VALUE
            ? undefined
            : state.cardTransactionsFilters?.status,
        dateFrom: state.cardTransactionsFilters?.dateFrom
          ? state.cardTransactionsFilters.dateFrom
          : stringToDate({ date: subMonths(new Date(), 1).toISOString(), formatExp: "yyyy-MM-dd'T'00:00:00" }),
        dateTo: state.cardTransactionsFilters?.dateTo
          ? state.cardTransactionsFilters.dateTo
          : stringToDate({ date: new Date().toISOString(), formatExp: "yyyy-MM-dd'T'23:59:59" }),
        meta: { timestamp },
      },
    },
    shouldFetchCardTransactions,
  );

  useEffect(() => {
    if (cardDetailsData && isEmpty(state.selectedCard)) {
      dispatch(
        action(CardDetailsContextActionTypes.SetSelectedCard, {
          card: cardDetailsData,
          timestamp: Date.now().toString(),
        }),
      );
    }
  }, [cardDetailsData]);

  // Initialize selected card from URL params
  useEffect(() => {
    if (cardId && cardListData) {
      const selectedCard = cardListData.find(card => card.cardId === cardId);
      if (Boolean(selectedCard)) {
        dispatch(
          action(CardDetailsContextActionTypes.SetSelectedCard, {
            card: selectedCard,
            timestamp: Date.now().toString(),
          }),
        );
        dispatch(
          action(
            CardDetailsContextActionTypes.SetSelectedPaymentCardIdentifications,
            selectedCard?.paymentCardIdentifications,
          ),
        );
      } else {
        dispatch(
          action(CardDetailsContextActionTypes.SetSelectedPaymentCardIdentifications, [
            {
              type: 'DxbCardIdentifier',
              identifier: {
                value: cardId,
                issuingAuthority: 'DXB',
              },
            },
          ]),
        );
      }
    }
  }, [cardId, cardListData]);

  const onCardTransactionSelect = useCallback(
    (transactionId: string) => {
      if (!cardTransactionsData) {
        return;
      }

      const selectedCardTransaction = cardTransactionsData.transactions.find(
        transaction => transaction.transactionId === transactionId,
      );
      dispatch(action(CardDetailsContextActionTypes.SetSelectedCardTransaction, selectedCardTransaction));
    },
    [cardTransactionsData],
  );

  // Initialize selected card transaction from URL param transactionNo
  useEffect(() => {
    if (transactionNo && cardTransactionsData) {
      onCardTransactionSelect(transactionNo);
    }
  }, [transactionNo, cardTransactionsData, onCardTransactionSelect]);

  const onSetSelectedCard = useCallback(
    (card: Nullable<Card>, cardAction: Nullable<CardAction>) => {
      if (!cardListData) {
        return;
      }
      dispatch(
        action(CardDetailsContextActionTypes.SetSelectedCard, {
          card,
          timestamp: Date.now().toString(),
        }),
      );
      dispatch(
        action(CardDetailsContextActionTypes.SetSelectedCardAction, {
          action: cardAction,
          timestamp: Date.now().toString(),
        }),
      );
      dispatch(action(CardDetailsContextActionTypes.SetCardTransactionsFilters, { pageNumber: 0, pageSize: 10 }));
    },
    [cardListData],
  );

  const onCardTransactionListFiltersChange = useCallback((filters: CardTransactionListFilters) => {
    if (filters.dateFrom && filters.dateTo) {
      filters.dateFrom = stringToDate({ date: filters.dateFrom, formatExp: "yyyy-MM-dd'T'00:00:00" });
      filters.dateTo = stringToDate({ date: filters.dateTo, formatExp: "yyyy-MM-dd'T'23:59:59" });
    }

    let key: keyof CardTransactionListFilters;
    for (key in filters) {
      !filters[key] && delete filters[key];
    }
    if (!filters.pageNumber) {
      filters.pageNumber = 0;
    }

    filters.pageSize = 10;

    setTimestamp(new Date().toISOString());
    window.scrollTo(0, 0);

    dispatch(action(CardDetailsContextActionTypes.SetCardTransactionsFilters, filters));
  }, []);

  const shouldRenderChildren = cardDetailsData || cardListData;

  return (
    <CardDetailsContext.Provider
      value={{
        ...state,
        cardDetailsData,
        loadingState: {
          isLoadingCardTransactionsData,
          isLoadingCardDetailsData,
        },
        errorState: {
          cardTransactionsDataError,
          cardDetailsDataError,
        },
        cardTransactionsData: cardTransactionsData?.transactions,
        cardTransactionsPagination: cardTransactionsData?.pagination || state.cardTransactionsPagination,
        onSetSelectedCard,
        onCardTransactionSelect,
        onCardTransactionListFiltersChange,
        refetchCardsDetailsDataMutation,
        refetchCardsTransactionsDataMutation,
      }}>
      <CardDetailsDispatchContext.Provider value={dispatch}>
        {isLoadingCardDetailsData && (
          <Box style={{ height: '100vh', position: 'absolute', top: '50%', right: '50%' }}>
            <ECOMMBXLoader size='full' verticalSpacing='large' />
          </Box>
        )}
        {shouldRenderChildren && children}
      </CardDetailsDispatchContext.Provider>
    </CardDetailsContext.Provider>
  );
};

const CardDetailsWrapper: FC<{ shouldNotFetchCardTransactions?: boolean }> = ({
  shouldNotFetchCardTransactions,
  children,
}) => {
  const cardListData = useCards();
  return (
    <CardDetailsProvider cardListData={cardListData} shouldNotFetchCardTransactions={shouldNotFetchCardTransactions}>
      {children}
    </CardDetailsProvider>
  );
};
export { CardDetailsProvider, CardDetailsWrapper };
